// ============================================================================
// Stages — one flat table per Allocation, in sequence order.
//
// Replaces the Route/rail timeline. That drawing derived a Route (Nodes joined by
// Rails with a proportional Progress line) which had to be read before the stages
// could be, hid every leg behind a collapsed Rail, and left the reader to map its
// vocabulary onto the stages the rest of the system talks about. A table shows the
// same legs, in order, all at once — and answers the ops question ("which leg is
// red, and what is holding it") in one glance.
//
// Each row expands to its own StageChecklist, exactly as the Rail's inner table did,
// so nothing that was reachable before has been lost. Cancelled and reverse (return)
// legs are ordinary rows here rather than special cases off to one side.
// ============================================================================
import { Button, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React from "react";
import { LogisticsDocument, OperationStage, SalesOrderDetail } from "../types";
import DocPdfButtons, { hasDocPdfs } from "./doc_pdf_buttons";
import { hasStageActions, StageActions } from "./escalation_actions";
import StageChecklist from "./stage_checklist";
import StatusTag from "./status_tag";

const humanize = (v: unknown) => {
  const spaced = String(v ?? "").replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Compact per-stage step progress — "3/6", tinted red if any sub-step failed and amber if any is
// blocked, so a stuck leg is legible without expanding. Skipped steps don't count.
const StepProgressCell: React.FC<{ stage: OperationStage }> = ({ stage }) => {
  const steps = stage.checklist ?? [];
  if (steps.length === 0) return <span style={{ color: "#ccc" }}>—</span>;
  const counted = steps.filter((s) => s.state !== "skipped");
  const done = counted.filter((s) => s.state === "done").length;
  const failed = counted.some((s) => s.state === "failed");
  const blocked = counted.some((s) => s.state === "blocked");
  const color = failed
    ? "#cf1322"
    : blocked
    ? "#d48806"
    : done === counted.length
    ? "#389e0d"
    : undefined;
  return (
    <Tooltip title="Sub-steps done / total — expand the row for the full checklist">
      <span style={{ color, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {done}/{counted.length}
      </span>
    </Tooltip>
  );
};

const ZohoDocsCell: React.FC<{ stage: OperationStage }> = ({ stage }) => {
  if (stage.zoho_docs.length === 0) return <span style={{ color: "#ccc" }}>—</span>;
  return (
    <Space size={[4, 4]} wrap>
      {stage.zoho_docs.map((d) => {
        const typeLabel = d.doc_type.replace(/_/g, " ");
        // Show the human doc number (e.g. a picklist's OMS-PICK-579) alongside the type so ops can
        // look the doc up by the key it actually carries; fall back to just the type when unstamped.
        const label = d.number ? `${typeLabel} · ${d.number}` : typeLabel;
        return d.url ? (
          <a key={d.zoho_doc_id} href={d.url} target="_blank" rel="noopener noreferrer">
            <Tag color="volcano" style={{ marginInlineEnd: 0 }}>
              {label} ↗
            </Tag>
          </a>
        ) : (
          <Tag key={d.zoho_doc_id} style={{ marginInlineEnd: 0 }}>
            {label}
          </Tag>
        );
      })}
    </Space>
  );
};

const LogisticsCell: React.FC<{
  stage: OperationStage;
  onOpenLogistics: (bookings: LogisticsDocument[], label: string) => void;
}> = ({ stage, onOpenLogistics }) => {
  const activeBooking = stage.logistics.find((b) => b.is_active) || stage.logistics[0];
  if (!activeBooking) return <span style={{ color: "#ccc" }}>—</span>;
  return (
    <Tag
      color="cyan"
      style={{ cursor: "pointer" }}
      onClick={() =>
        onOpenLogistics(
          stage.logistics,
          `Logistics — ${stage.operation_type} (leg #${stage.sequence})`
        )
      }
    >
      🚚 {activeBooking.provider_ref || "—"} ↗
    </Tag>
  );
};

// Where the leg runs. The endpoints were the timeline's Nodes; as a column they read in the same
// place as everything else about the leg, instead of being the frame the legs hung off.
const RouteCell: React.FC<{ stage: OperationStage }> = ({ stage }) => {
  const from = stage.source?.label;
  const to = stage.destination?.label;
  if (!from && !to) return <span style={{ color: "#ccc" }}>—</span>;
  return (
    <span style={{ fontSize: 12, color: "#595959" }}>
      {from || "—"} <span style={{ color: "#bfbfbf" }}>→</span> {to || "—"}
    </span>
  );
};

const stageColumns = (
  onOpenLogistics: (bookings: LogisticsDocument[], label: string) => void,
  onChanged?: (so: SalesOrderDetail) => void,
  reallocateLineId?: number
): ColumnsType<OperationStage> => [
  {
    // Sequence is the leg's position in the plan; the stage id underneath is what backend logs,
    // Metabase and a support request all refer to.
    title: "#",
    dataIndex: "sequence",
    width: 64,
    render: (v: number, stage) => (
      <>
        <div>{v}</div>
        <Tooltip title="Stage id">
          <span style={{ fontSize: 11, color: "#8c8c8c" }}>#{stage.id}</span>
        </Tooltip>
      </>
    ),
  },
  {
    title: "Operation",
    dataIndex: "operation_type",
    width: 150,
    render: (v: string, stage) => (
      <Space size={4}>
        <span>{humanize(v)}</span>
        {stage.direction && stage.direction !== "forward" ? (
          <Tag color="gold" style={{ marginInlineEnd: 0 }}>
            ↩ {stage.direction}
          </Tag>
        ) : null}
      </Space>
    ),
  },
  {
    title: "Route",
    key: "route",
    width: 220,
    render: (_, stage) => <RouteCell stage={stage} />,
  },
  {
    title: "Status",
    dataIndex: "status",
    width: 120,
    render: (s, stage) => (
      <StatusTag
        status={s}
        tooltip={stage.error_message || stage.runtime_error || stage.hold_reason}
      />
    ),
  },
  {
    title: "Steps",
    key: "steps",
    width: 70,
    render: (_, stage) => <StepProgressCell stage={stage} />,
  },
  {
    title: "Due (SLA)",
    dataIndex: "due_at",
    width: 120,
    render: (v: string | null, stage) => {
      if (!v) return <span style={{ color: "#ccc" }}>—</span>;
      const due = dayjs(v);
      const open = !["completed", "cancelled"].includes(stage.issue_state);
      const overdue = open && due.isBefore(dayjs());
      return (
        <Tooltip title={due.format("DD MMM YYYY, HH:mm")}>
          <span
            style={{
              color: overdue ? "#cf1322" : undefined,
              fontWeight: overdue ? 600 : undefined,
            }}
          >
            {due.format("DD MMM")}
            {overdue ? " ⚠︎" : ""}
          </span>
        </Tooltip>
      );
    },
  },
  { title: "Zoho", key: "zoho", width: 160, render: (_, stage) => <ZohoDocsCell stage={stage} /> },
  {
    title: "Logistics",
    key: "logistics",
    render: (_, stage) => <LogisticsCell stage={stage} onOpenLogistics={onOpenLogistics} />,
  },
  {
    title: "Actions",
    key: "actions",
    width: 160,
    render: (_: unknown, stage: OperationStage) => {
      const hasPdfs = hasDocPdfs(stage.zoho_docs);
      const hasActions = !!onChanged && hasStageActions(stage);
      if (!hasPdfs && !hasActions) return <span style={{ color: "#ccc" }}>—</span>;
      return (
        <Space size={4} wrap>
          <DocPdfButtons docs={stage.zoho_docs} />
          {onChanged ? (
            <StageActions stage={stage} onDone={onChanged} />
          ) : null}
        </Space>
      );
    },
  },
];

const StageTable: React.FC<{
  stages: OperationStage[];
  onOpenLogistics?: (bookings: LogisticsDocument[], label: string) => void;
  onChanged?: (so: SalesOrderDetail) => void;
}> = ({ stages, onOpenLogistics, onChanged }) => {
  const openLogistics = onOpenLogistics ?? (() => undefined);
  // Sequence order, with reverse legs after the forward ones — the order the work happens in.
  const rows = [...(stages ?? [])].sort((a, b) => {
    const dir = Number(a.direction !== "forward") - Number(b.direction !== "forward");
    return dir !== 0 ? dir : a.sequence - b.sequence;
  });

  return (
    <Table
      rowKey="id"
      size="small"
      pagination={false}
      columns={stageColumns(openLogistics, onChanged)}
      dataSource={rows}
      scroll={{ x: "max-content" }}
      // A cancelled leg stays in the list — it is part of what happened to this allocation — but
      // greys out so it never competes with the live ones for attention.
      rowClassName={(stage) => (stage.issue_state === "cancelled" ? "oms-stage-cancelled" : "")}
      onRow={(stage) =>
        ({
          "data-testid": "stage-row",
          "data-stage-id": stage.id,
        } as React.HTMLAttributes<HTMLTableRowElement>)
      }
      expandable={{
        expandedRowRender: (stage) => <StageChecklist steps={stage.checklist} />,
        rowExpandable: (stage) =>
          Array.isArray(stage.checklist) && stage.checklist.length > 0,
      }}
    />
  );
};

export default StageTable;
